import React, { useState, useEffect } from 'react';
import { FiChevronUp, FiChevronDown, FiChevronLeft, FiChevronRight, FiPlusCircle, FiTrash2, FiXCircle } from 'react-icons/fi';
import userRoleUtils from '../utils/userRoleUtils';
import { cleaningQuantityStep } from '../utils/cleaningQuantity';

const DefaultOrder = ({
  categories,
  products,
  packs,
  defaultOrder,
  onQuantityChange,
  onRentTypeChange,
  onAddExtraItem,
  onRemoveExtraItem,
  onDiscountChange
}) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showExtraItemForm, setShowExtraItemForm] = useState(false);
  const [extraItem, setExtraItem] = useState({
    name: '',
    qty: 1,
    price: 0
  });
  const savedDiscount = defaultOrder['discount-default'] || {};
  const savedDiscountType = savedDiscount.discount_type || 'fixed';
  const savedDiscountValue = savedDiscount.discount_value || 0;
  const [discount, setDiscount] = useState({
    enabled: Boolean(savedDiscountValue),
    type: savedDiscountType,
    value: savedDiscountValue
  });

  useEffect(() => {
    setDiscount({
      enabled: Boolean(savedDiscountValue),
      type: savedDiscountType,
      value: savedDiscountValue
    });
  }, [savedDiscountType, savedDiscountValue]);

  const categoryOrder = {
    'Linen Hire': 1,
    'Essentials': 2,
    'Cleaning Service': 3,
    'Consumable': 4,
    'Baby Equipment Hire': 5
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const orderA = categoryOrder[a.category_title] || 999;
    const orderB = categoryOrder[b.category_title] || 999;
    return orderA - orderB;
  });

  const handleExtraItemChange = (field, value) => {
    setExtraItem({ ...extraItem, [field]: value });
  };

  const isAdmin = () =>{
    // Replace with actual admin check logic
    return userRoleUtils.isAdmin();
  }
  const handleAddExtraItem = () => {
    if (!extraItem.name) {
      alert('Please enter a name for the extra item');
      return;
    }

    // Call the parent handler with extra item data
    if (onAddExtraItem) {
      onAddExtraItem({
        ...extraItem,
        id: `extra-${Date.now()}`, // Generate a unique ID
        isExtra: true
      });
    }

    // Reset form
    setExtraItem({
      name: '',
      qty: 1,
      price: 0
    });
    setShowExtraItemForm(false);
  };

  const updateDiscount = (changes) => {
    const next = { ...discount, ...changes };
    setDiscount(next);
    if (onDiscountChange) {
      onDiscountChange(next);
    }
  };

  return (
    <div className="bg-blue-100 p-6 rounded-lg border shadow mt-6">
      <h3 className="text-2xl font-semibold mb-4 flex items-center">
        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        Default Order
      </h3>
      <p className="text-sm text-gray-600 mb-4">Disclaimer: All prices are including VAT under the total amount</p>
      
      {/* Add Extra Item Button */}
      {isAdmin() && <div className="mb-4">
        <button
          className={`flex items-center px-4 py-2 rounded-lg ${showExtraItemForm ? 'bg-red-100 text-red-700' : 'bg-green-600 text-white'}`}
          onClick={() => setShowExtraItemForm(!showExtraItemForm)}
        >
          {showExtraItemForm ? (
            <>
              <FiXCircle className="mr-2" />
              Cancel Extra Item
            </>
          ) : (
            <>
              <FiPlusCircle className="mr-2" />
              Add Custom Extra Item
            </>
          )}
        </button>
      </div>}

      {isAdmin() && (
        <div className="bg-white p-4 rounded-lg shadow mb-6 border border-gray-300">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <label className="flex items-center gap-2 font-semibold">
              <input
                type="checkbox"
                checked={discount.enabled}
                onChange={(e) => updateDiscount({ enabled: e.target.checked })}
              />
              Apply discount to this property's default order
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount type</label>
              <select
                value={discount.type}
                disabled={!discount.enabled}
                onChange={(e) => updateDiscount({ type: e.target.value })}
                className="p-2 border rounded"
              >
                <option value="fixed">Fixed amount (£)</option>
                <option value="percentage">Percentage (%)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount value</label>
              <input
                type="number"
                min="0"
                max={discount.type === 'percentage' ? 100 : undefined}
                step="0.01"
                value={discount.value}
                disabled={!discount.enabled}
                onChange={(e) => updateDiscount({ value: Math.max(0, parseFloat(e.target.value) || 0) })}
                className="p-2 border rounded w-40"
              />
            </div>
          </div>
        </div>
      )}

      {/* Extra Item Form */}
      {showExtraItemForm && (
        <div className="bg-white p-4 rounded-lg shadow mb-6 border border-gray-300">
          <h4 className="font-semibold mb-3">Add Custom Extra Item</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Item Name
              </label>
              <input
                type="text"
                value={extraItem.name}
                onChange={(e) => handleExtraItemChange('name', e.target.value)}
                placeholder="Enter custom item name"
                className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price (£)
              </label>
              <input
                type="number"
                value={extraItem.price}
                onChange={(e) => handleExtraItemChange('price', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity
              </label>
              <div className="flex items-center">
                <button
                  onClick={() => {
                    if (extraItem.qty >1) {
                      handleExtraItemChange('qty', extraItem.qty -1);
                    }
                  }}
                  className="text-navy-900 border px-2 py-1 rounded-l"
                >
                  <FiChevronLeft />
                </button>
                <input
                  type="number"
                  value={extraItem.qty}
                  onChange={(e) => handleExtraItemChange('qty', parseFloat(e.target.value) || 0)}
                  step="0.5"
                  min="0"
                  className="mx-1 w-full text-center border py-1"
                />
                <button
                  onClick={() => handleExtraItemChange('qty', extraItem.qty + 1)}
                  className="text-navy-900 border px-2 py-1 rounded-r"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>

            <div className="col-span-1 md:col-span-3 flex justify-end">
              <button
                className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center"
                onClick={handleAddExtraItem}
              >
                <FiPlusCircle className="mr-2" />
                Add Extra Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Display any extra items */}
      {Object.values(defaultOrder)
        .filter(item => item.isExtra)
        .length > 0 && (
        <div className="bg-yellow-50 p-4 rounded-lg shadow mb-6 border border-yellow-200">
          <h4 className="font-semibold mb-3 flex items-center">
            <FiPlusCircle className="mr-2 text-yellow-600" />
            Extra Items
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(defaultOrder)
              .filter(([_, item]) => item.isExtra)
              .map(([id, item]) => (
                <ExtraItemDisplay
                  key={id}
                  id={id}
                  item={item}
                  onQuantityChange={onQuantityChange}
                  onRemove={onRemoveExtraItem}
                />
              ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {sortedCategories.map((category) => (
          <CategorySection
            key={category.id}
            category={category}
            products={products[category.id] || []}
            packs={category.id === 1 ? packs : []}
            defaultOrder={defaultOrder}
            onQuantityChange={onQuantityChange}
            onRentTypeChange={onRentTypeChange}
            isExpanded={expandedCategory === category.id}
            onToggle={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
          />
        ))}
      </div>
    </div>
  );
};

// New component for displaying extra items
const ExtraItemDisplay = ({ id, item, onQuantityChange, onRemove }) => {
  const [customQuantity, setCustomQuantity] = useState(item.qty.toString());

  useEffect(() => {
    setCustomQuantity(item.qty.toString());
  }, [item.qty]);

  const handleCustomQuantityChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and a single decimal point
    if (/^-?\d*\.?\d*$/.test(value) || value === '') {
      setCustomQuantity(value);
    }
  };

  const handleBlur = () => {
    // Convert to number and handle precision
    const numValue = parseFloat(customQuantity) || 0;
    // Round to 2 decimal places
    const roundedValue = Math.round(numValue * 100) / 100;
    setCustomQuantity(roundedValue.toString());
    
    // Update quantity in parent component
    const diff = roundedValue - item.qty;
    if (diff !== 0) {
      onQuantityChange(id, diff, true, item.price);
    }
  };

  const incrementQuantity = (increment) => {
    // Calculate new quantity with the increment
    const currentQty = parseFloat(item.qty) || 0;
    const newQty = currentQty + increment;
    
    // Ensure non-negative quantity
    if (newQty >= 0) {
      onQuantityChange(id, increment, true, item.price);
      setCustomQuantity(newQty.toString());
    }
  };

  return (
    <div className="bg-white p-4 shadow border border-yellow-200">
      <div className="flex items-center mb-2">
        <div className="flex items-center justify-center w-16 h-16 bg-yellow-100 rounded mr-4">
          <FiPlusCircle className="text-yellow-600 text-2xl" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium">{item.name}</h4>
            {onRemove && (
              <button
                type="button"
                aria-label={`Remove ${item.name}`}
                title="Remove extra item"
                onClick={() => onRemove(id)}
                className="p-1 text-red-600 hover:text-red-800"
              >
                <FiTrash2 />
              </button>
            )}
          </div>
          <p className="text-sm text-gray-600">Extra Item</p>
          <div className="flex justify-between items-center mt-2">
            <span className="text-navy-600 font-bold">{`£${parseFloat(item.price).toFixed(2)}`}</span>
           
            <div className="flex items-center">
              <button
                onClick={() => incrementQuantity(-1)}
                className="text-navy-900 border px-2 py-1"
              >
                <FiChevronLeft />
              </button>
              <input
                type="text"
                value={customQuantity}
                onChange={handleCustomQuantityChange}
                onBlur={handleBlur}
                className="mx-1 w-16 text-center border py-1"
                style={{ minWidth: '50px' }}
              />
              <button
                onClick={() => incrementQuantity(1)}
                className="text-navy-900 border px-2 py-1"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CategorySection = ({
  category,
  products,
  packs,
  defaultOrder,
  onQuantityChange,
  onRentTypeChange,
  isExpanded,
  onToggle,
}) => {
  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <button
        className="w-full p-4 text-left font-medium flex justify-between items-center"
        onClick={onToggle}
      >
        {category.category_title}
        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
      </button>
      {isExpanded && (
        <div className="p-4 border-t">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packs.map((pack) => (
              <PackItem
                key={pack.id}
                pack={pack}
                defaultOrder={defaultOrder}
                onQuantityChange={onQuantityChange}
              />
            ))}
            {products.map((product) => (
              <ProductItem
                key={product.id}
                product={product}
                categoryTypeId={category.category_type_id}
                defaultOrder={defaultOrder}
                onQuantityChange={onQuantityChange}
                onRentTypeChange={onRentTypeChange}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PackItem = ({ pack, defaultOrder, onQuantityChange }) => {
  const quantity = defaultOrder[pack.id]?.qty || 0;
  const [customQuantity, setCustomQuantity] = useState(quantity.toString());

  // Update internal state when prop changes
  useEffect(() => {
    setCustomQuantity(quantity.toString());
  }, [quantity]);

  const handleCustomQuantityChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and a single decimal point
    if (/^-?\d*\.?\d*$/.test(value) || value === '') {
      setCustomQuantity(value);
    }
  };

  const handleBlur = () => {
    // Convert to number and handle precision
    const numValue = parseFloat(customQuantity) || 0;
    // Round to 2 decimal places
    const roundedValue = Math.round(numValue * 100) / 100;
    setCustomQuantity(roundedValue.toString());
    
    // Update quantity in parent component
    const diff = roundedValue - quantity;
    if (diff !== 0) {
      onQuantityChange(pack.id, diff, false, pack.price);
    }
  };

  const incrementQuantity = (increment) => {
    // Calculate new quantity with the increment
    const currentQty = parseFloat(quantity) || 0;
    const newQty = currentQty + increment;
    
    // Ensure non-negative quantity
    if (newQty >= 0) {
      onQuantityChange(pack.id, increment, false, pack.price);
      setCustomQuantity(newQty.toString());
    }
  };

  return (
    <div className="bg-white p-4 shadow border">
      <div className="flex items-center mb-2">
        <img
          src={pack.image || "/ico/gglogolarge.svg"}
          alt={pack.name}
          className="w-16 h-16 object-cover rounded mr-4"
        />
        <div>
          <h4 className="font-medium">{pack.name}</h4>
          <p className="text-sm text-gray-600">{pack.description}</p>
          <div className="flex justify-between items-center mt-2">
            <span className="text-navy-600 font-bold"> {`£${!isNaN(+pack.price) ? pack.price : 
              typeof pack.price === 'number' ? pack.price.toFixed(2) : pack.price}`}</span>
           
            <div className="flex items-center">
              <button
                onClick={() => incrementQuantity(-1)}
                className="text-navy-900 border px-2 py-1"
              >
                <FiChevronLeft />
              </button>
              <input
                type="text"
                value={customQuantity}
                onChange={handleCustomQuantityChange}
                onBlur={handleBlur}
                className="mx-1 w-16 text-center border py-1"
                style={{ minWidth: '50px' }}
              />
              <button
                onClick={() => incrementQuantity(1)}
                className="text-navy-900 border px-2 py-1"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductItem = ({
  product,
  categoryTypeId,
  defaultOrder,
  onQuantityChange,
  onRentTypeChange,
}) => {
  const quantity = defaultOrder[product.id]?.qty || 0;
  const quantityStep = cleaningQuantityStep(product);
  const [customQuantity, setCustomQuantity] = useState(quantity.toString());
  const selectedRentType = defaultOrder[product.id]?.rentType || "3days";

  // Update internal state when prop changes
  useEffect(() => {
    setCustomQuantity(quantity.toString());
  }, [quantity]);

  const rentTypes = [
    { key: "3days", label: "3 Days", priceKey: "priceone" },
    { key: "1week", label: "1 Week", priceKey: "pricetwo" },
    { key: "2weeks", label: "2 Weeks", priceKey: "pricethree" },
    { key: "1month", label: "1 Month", priceKey: "pricefour" },
  ];

  // Ensure product_price is properly parsed as a float
  const productPrice = parseFloat(product.product_price);

  const handleCustomQuantityChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and a single decimal point
    if (/^-?\d*\.?\d*$/.test(value) || value === '') {
      setCustomQuantity(value);
    }
  };

  const handleBlur = () => {
    // Convert to number and handle precision
    const numValue = parseFloat(customQuantity) || 0;
    // Round to 2 decimal places
    const roundedValue = Math.round(numValue * 100) / 100;
    setCustomQuantity(roundedValue.toString());
    
    // Update quantity in parent component if there's a change
    const diff = roundedValue - quantity;
    if (diff !== 0) {
      const price = categoryTypeId === 1 && !isNaN(productPrice)
        ? productPrice
        : product[rentTypes.find(rt => rt.key === selectedRentType).priceKey];
        
      onQuantityChange(product.id, diff, true, price);
    }
  };

  const incrementQuantity = (increment) => {
    // Calculate new quantity with the increment
    const currentQty = parseFloat(quantity) || 0;
    const newQty = currentQty + increment;
    
    // Ensure non-negative quantity
    if (newQty >= 0) {
      const price = categoryTypeId === 1 && !isNaN(productPrice)
        ? productPrice
        : product[rentTypes.find(rt => rt.key === selectedRentType).priceKey];
        
      onQuantityChange(product.id, increment, true, price);
      setCustomQuantity(newQty.toString());
    }
  };

  return (
    <div className="bg-white p-4 shadow border">
      <div className="flex items-center mb-2">
        <img
          src={product.image || "/ico/gglogolarge.svg"}
          alt={product.product_title}
          className="w-16 h-16 object-cover rounded mr-4"
        />
        <div style={{ width: "100%" }}>
          <h4 className="font-medium">{product.product_title}</h4>
          <p
            style={{ minHeight: 30, width: "100%" }}
            className="text-sm text-gray-600"
          >
            {product.product_description || product.product_title}
          </p>
          {(categoryTypeId === 1 && !isNaN(productPrice)) ? (
            <div className="flex justify-between items-center mt-2">
              <span className="text-navy-900 font-bold">{`£${productPrice.toFixed(2)}`}</span>
              <div className="flex items-center">
                <button
                  onClick={() => incrementQuantity(-quantityStep)}
                  className="text-navy-500 border px-2 py-1"
                >
                  <FiChevronLeft />
                </button>
                <input
                  type="text"
                  value={customQuantity}
                  onChange={handleCustomQuantityChange}
                  aria-label={`${product.product_title} quantity`}
                  onKeyDown={(event) => {
                    if (quantityStep === 0.5 && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
                      event.preventDefault();
                      incrementQuantity(event.key === 'ArrowUp' ? quantityStep : -quantityStep);
                    }
                  }}
                  onBlur={handleBlur}
                  className="mx-1 w-16 text-center border py-1"
                  style={{ minWidth: '50px' }}
                />
                <button
                  onClick={() => incrementQuantity(quantityStep)}
                  className="text-navy-500 border px-2 py-1 rounded"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <select
                value={selectedRentType}
                onChange={(e) => onRentTypeChange(product.id, e.target.value)}
                className="w-full p-2 border rounded mb-2"
              >
                {rentTypes.map(({ key, label, priceKey }) => (
                  <option key={key} value={key}>
                    {`${label}: £${product[priceKey]?.toFixed(2)}`}
                  </option>
                ))}
              </select>
              <div className="flex justify-between items-center">
                <span className="text-navy-600 font-bold">{`£${product[
                  rentTypes.find((rt) => rt.key === selectedRentType).priceKey
                ].toFixed(2)}`}</span>
                <div className="flex items-center">
                  <button
                    onClick={() => incrementQuantity(-quantityStep)}
                    className="text-gray-500 border px-2 py-1"
                  >
                    <FiChevronLeft />
                  </button>
                  <input
                    type="text"
                    value={customQuantity}
                    onChange={handleCustomQuantityChange}
                  aria-label={`${product.product_title} quantity`}
                  onKeyDown={(event) => {
                    if (quantityStep === 0.5 && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
                      event.preventDefault();
                      incrementQuantity(event.key === 'ArrowUp' ? quantityStep : -quantityStep);
                    }
                  }}
                    onBlur={handleBlur}
                    className="mx-1 w-16 text-center border py-1"
                    style={{ minWidth: '50px' }}
                  />
                  <button
                    onClick={() => incrementQuantity(quantityStep)}
                    className="text-gray-500 border px-2 py-1"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DefaultOrder;
